import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return createPortal(
    <div
      className="confirm-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
    >
      <div className="confirm-box animate-fade-in" role="dialog" aria-modal="true">
        <h3>{title}</h3>
        <div className="confirm-msg">{message}</div>
        <div className="confirm-actions">
          <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </button>
          <button
            className={`btn btn-sm ${danger ? "btn-danger" : "btn-primary"}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner" />…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmOpts {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  /** The action to run on confirm; should handle its own errors (e.g. toast). */
  run: () => Promise<void> | void;
}

/**
 * Imperative confirm dialog. `ask(opts)` opens it; render `dialog` somewhere in
 * the tree. The action runs with a loading state, then the dialog closes.
 */
export function useConfirm() {
  const [state, setState] = useState<ConfirmOpts | null>(null);
  const [loading, setLoading] = useState(false);

  const ask = (opts: ConfirmOpts) => setState(opts);

  async function onConfirm() {
    if (!state) return;
    setLoading(true);
    try {
      await state.run();
    } finally {
      setLoading(false);
      setState(null);
    }
  }

  const dialog = (
    <ConfirmDialog
      open={!!state}
      title={state?.title ?? ""}
      message={state?.message}
      confirmLabel={state?.confirmLabel}
      danger={state?.danger}
      loading={loading}
      onConfirm={onConfirm}
      onCancel={() => {
        if (!loading) setState(null);
      }}
    />
  );

  return { ask, dialog };
}
